/* eslint-disable no-console -- this plugin intentionally wraps console methods for RUM capture */
import type { Plugin, PluginContext } from 'aws-rum-web';

import {
  CONSOLE_LOG_EVENT_TYPE,
  fitEventPayload,
  serializeConsoleArgs,
} from './contentCaptureUtils';

type ConsoleLevel = 'log' | 'info' | 'debug' | 'warn' | 'error';

const CONSOLE_LEVELS: ConsoleLevel[] = ['log', 'info', 'debug', 'warn', 'error'];
const RUM_DEBUG_PREFIX = '[aws-rum-web:';

export class RumConsoleCapturePlugin implements Plugin {
  private context!: PluginContext;
  private enabled = false;
  private isRecording = false;
  private originals: Partial<Record<ConsoleLevel, (...args: unknown[]) => void>> = {};

  load(context: PluginContext): void {
    this.context = context;
    this.enable();
  }

  enable(): void {
    if (this.enabled || typeof window === 'undefined') return;
    this.enabled = true;

    CONSOLE_LEVELS.forEach((level) => {
      this.originals[level] = console[level].bind(console);
      console[level] = (...args: unknown[]) => {
        this.originals[level]!(...args);
        this.recordConsole(level, args);
      };
    });
  }

  disable(): void {
    if (!this.enabled || typeof window === 'undefined') return;
    this.enabled = false;

    CONSOLE_LEVELS.forEach((level) => {
      const original = this.originals[level];
      if (original) {
        console[level] = original as typeof console.log;
      }
    });
    this.originals = {};
  }

  getPluginId(): string {
    return 'RumConsoleCapturePlugin';
  }

  private recordConsole(level: ConsoleLevel, args: unknown[]): void {
    if (this.isRecording) return;

    const message = serializeConsoleArgs(args);
    if (message.startsWith(RUM_DEBUG_PREFIX)) return;

    this.isRecording = true;
    try {
      this.context.record(
        CONSOLE_LOG_EVENT_TYPE,
        fitEventPayload({
          level,
          message,
          timestamp: Date.now(),
        })
      );
    } finally {
      this.isRecording = false;
    }
  }
}
